import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import { Seo } from "@/components/site/Seo";
import { Link } from "react-router-dom";

const CheckMark = () => (
  <svg width="14" height="14" viewBox="0 0 18 18" aria-hidden="true">
    <path d="M4 9.5 7.5 13 14 5" fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Security = () => (
  <>
    <Seo
      title="Clearview — security"
      description="Read-only by architecture. Clearview reads your numbers — it cannot move money, pay anyone, or change anything in your books."
      path="/security"
    />
    <Header />

    <section className="ph">
      <div className="container">
        <Reveal>
          <div className="shield">
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M12 2 4 5v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V5l-8-3Z" stroke="#fff" strokeWidth="2" strokeLinejoin="round" />
              <path d="M9 12l2 2 4-4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="eyebrow eyebrow-light">Security &amp; trust</div>
          <h1 style={{ marginTop: 14 }}>
            Read-only<br /><span className="gtext">by architecture.</span>
          </h1>
          <p className="lead">
            Clearview reads your numbers to write you a read. It cannot move money, pay anyone, or change a thing in your books — not by policy, by design.
          </p>
        </Reveal>
      </div>
    </section>

    <section className="pad">
      <div className="container">
        <Reveal className="scards">
          <div className="scard">
            <div className="ic">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M12 3l7 3v5c0 4.5-3 7.3-7 8.5C8 21.3 5 18.5 5 14V6l7-3Z" stroke="#8B5CF6" strokeWidth="2" strokeLinejoin="round" />
              </svg>
            </div>
            <h3>It can't touch your money</h3>
            <p>The connection requests read scopes only. There is no path in the system that can initiate a payment or edit a ledger. The capability simply isn't there.</p>
          </div>
          <div className="scard">
            <div className="ic">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="4" y="10" width="16" height="11" rx="2" stroke="#3B93F2" strokeWidth="2" />
                <path d="M8 10V7a4 4 0 0 1 8 0v3" stroke="#3B93F2" strokeWidth="2" />
              </svg>
            </div>
            <h3>Encrypted, end to end</h3>
            <p>Your data is encrypted in transit and at rest, and held in Australia. Access is logged. We never sell it, and we never share it with your accounting vendor.</p>
          </div>
          <div className="scard">
            <div className="ic">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M6 18 18 6M9 6h9v9" stroke="#EC4899" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h3>Leave any time</h3>
            <p>Disconnect in a couple of clicks. Your access tokens are revoked immediately and your data is deleted on request. Nothing is held hostage.</p>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="band-grey pad">
      <div className="container narrow">
        <Reveal className="center">
          <div className="eyebrow">How the connection works</div>
          <h2 style={{ fontSize: "clamp(26px,4vw,38px)", color: "var(--navy)", marginTop: 14 }}>
            Two minutes, and nothing changes hands.
          </h2>
        </Reveal>
        <Reveal className="rows" style={{ marginTop: 36 }}>
          <div className="srow">
            <div className="ck"><CheckMark /></div>
            <div>
              <h3>You authorise read-only access</h3>
              <p>You connect through your accounting provider's own secure login (OAuth). You grant read access — Clearview never sees or stores your password.</p>
            </div>
          </div>
          <div className="srow">
            <div className="ck"><CheckMark /></div>
            <div>
              <h3>We read, we don't write</h3>
              <p>The tokens we hold can read your figures and nothing else. There is no write permission to revoke later — it was never requested.</p>
            </div>
          </div>
          <div className="srow">
            <div className="ck"><CheckMark /></div>
            <div>
              <h3>Your data stays yours</h3>
              <p>It's isolated per business, used only to write your reads, never pooled to train anything for anyone else, and never sold.</p>
            </div>
          </div>
          <div className="srow">
            <div className="ck"><CheckMark /></div>
            <div>
              <h3>You can see what it can see</h3>
              <p>A plain-English trust summary shows exactly which systems are connected, when they last synced, and what Clearview is reading.</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="pad">
      <div className="container">
        <Reveal>
          <div className="callout">
            <h2>Independence cuts both ways.</h2>
            <p>Because Clearview isn't your accounting vendor, it has no reason to keep your data or lock you in. The whole pitch is that it works for you — and the security model is built to prove it, not just say it.</p>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="pad" style={{ paddingTop: 0 }}>
      <div className="container">
        <Reveal>
          <div className="final">
            <div className="inner">
              <h2>Connect read-only.<br /><span className="gtext">See it read your business.</span></h2>
              <p>A free, no-pitch demo — and nothing it could ever move.</p>
              <Link className="pill" to="/apply" data-testid="security-final-cta">Book a free demo</Link>
              <p className="fine">A free, no-pitch demo · read-only · no lock-in</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <Footer />
  </>
);

export default Security;
